import { useEffect } from "react";
import { TRACKED_SECTIONS, trackSectionViewed } from "./eventHelpers";

const DEFAULT_THRESHOLDS = [0, 0.1, 0.25, 0.5, 0.75, 1];

const isMeaningfullyVisible = (entry, targetRatio) => {
  if (!entry.isIntersecting) {
    return false;
  }

  if (entry.intersectionRatio >= targetRatio) {
    return true;
  }

  // Tall sections may never reach 50% element visibility in a short viewport.
  // Treat "about half the viewport covered by the section" as equivalent.
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  if (viewportHeight <= 0) {
    return false;
  }

  return entry.intersectionRect.height >= viewportHeight * targetRatio;
};

/**
 * Observes landing-page sections and fires section_viewed once per section
 * when approximately 50% of the section (or viewport for tall sections) is visible.
 */
export const useSectionViewTracking = (
  sectionIds = TRACKED_SECTIONS,
  targetRatio = 0.5
) => {
  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (elements.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!isMeaningfullyVisible(entry, targetRatio)) {
            return;
          }
          const sectionId = entry.target.id;
          if (sectionId) {
            trackSectionViewed(sectionId);
          }
        });
      },
      { threshold: DEFAULT_THRESHOLDS }
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
    };
  }, [sectionIds, targetRatio]);
};
